import _ from 'lodash'
import { userCurrentInfo, userCurrentSysMenus } from '@/api/moudules/system'
import Vue from 'vue'
import Vuex from 'vuex'
import { E_IsSoonExpire, ICurrentUserInfo, IUserMenu } from '@/api/moudules/system.types'
import { getStorageValueWithTTL, loginCounter, storageValueWithTTL } from '@/utils/storage'
import { E_TimeUnit, toSeconds } from '@/utils/time'
import { E_YesNoNumber } from '@/api/types'
import { platformCompanyGetCompany, platformPortal } from '@/api/moudules/platformCompany'
import { E_PlatformPortalEntry, IPlatformCompanySettingData, IPlatformPortalData } from '@/api/moudules/platformCompany.types'
import VueI18n from 'vue-i18n'
import { E_Lang, T_Lang } from '@/i18n/types'
import { autoLogin } from '@/api/moudules/login'
import { markAuth } from '@/utils/auth'
import { IDiagnosisConclusionRepositorEditParams } from '@/api/moudules/diagnosis.types'
import { diagnosisConclusionRepositorList } from '@/api/moudules/diagnosis'
import { E_RouterNames } from '@/router/types'
import { Features } from '@/custom/features'

Vue.use(Vuex)

const InvalidMenuRouteNames = new Set(['', '#', E_RouterNames.__click])

const LargeScreenRouteNames = new Set<string>([
  E_RouterNames.largeScreenCompany,
  E_RouterNames.largeScreenNormal,
  E_RouterNames.largeScreen2,
  E_RouterNames.largeScreenLegacyNormal,
  E_RouterNames.largeScreenLegacyExt,
])

function collectMenuPermissions(menus: IUserMenu[], permissions: string[]) {
  if (_.isEmpty(menus)) {
    return []
  }
  _.each(menus, menu => {
    permissions.push(menu.perms)
    collectMenuPermissions(menu.children, permissions)
  })
}

export function firstMenuRouterName(menus: IUserMenu[], allowedRoutes?: Set<string>) {
  if (_.isEmpty(menus)) {
    return ''
  }
  for (const menu of menus) {
    const path = menu.path || ''
    if (!InvalidMenuRouteNames.has(path) && (!allowedRoutes || allowedRoutes.has(path))) {
      return path
    }
    const childRouterName = firstMenuRouterName(menu.children, allowedRoutes)
    if (childRouterName) {
      return childRouterName
    }
  }
  return ''
}

function defaultMenuRouterName(menus: IUserMenu[]) {
  return firstMenuRouterName(menus)
}

function isSuperAdmin(acount: ICurrentUserInfo) {
  if (!acount) {
    return false
  }
  return _.includes(acount.permissions, '*:*:*')
}

export function makeStore(i18n?: VueI18n) {
  const store = new Vuex.Store({
    state: {
      loading: false,
      account: null,
      menus: [],
      menuPermissionsLoaded: false,
      hasAlertSoonExpire: false,
      platformSetting: {} as IPlatformCompanySettingData,
      platformPortal: null as IPlatformPortalData,
      conclusionRepositorList: [] as IDiagnosisConclusionRepositorEditParams[],
      features: new Features(null),
    },
    getters: {
      isSuperAdmin(state) {
        return isSuperAdmin(state.account)
      },
      isAdmin(state) {
        if (!state.account) {
          return false
        }
        return state.account.user?.superId === E_YesNoNumber.Yes
      },
      hasLogin(state) {
        return Boolean(state.account)
      },
      currentUserNickName(state) {
        return _.get(state, 'account.user.nickName', '')
      },
      currentParentCompany(state) {
        return _.get(state, 'account.user.parentCompanyId')
      },
      currentParentCompanyName(state) {
        return _.get(state, 'account.user.parentCompanyName')
      },
      defaultMenuRouterName(state) {
        return defaultMenuRouterName(state.menus)
      },
      defaultMainMenuRouterName(state) {
        const menus = _.filter(state.menus, m => {
            return ![
                E_RouterNames.largeScreenCompany,
                E_RouterNames.largeScreenNormal,
                'largeScreen'
            ].includes(m.path)
        })
        return defaultMenuRouterName(menus)
      },
      monitorEntryRouterName(state, getters) {
        return getters.defaultMainMenuRouterName || getters.defaultMenuRouterName
      },
      largeScreenEntryRouterName(state, getters) {
        return firstMenuRouterName(state.menus, LargeScreenRouteNames) || getters.defaultMenuRouterName
      },
      loginEntryRouterName(state, getters) {
        const entry = state.platformPortal?.loginEntry
        if (entry === E_PlatformPortalEntry.Portal || entry === E_PlatformPortalEntry.None) {
          return E_RouterNames.platformPortal
        }
        if (entry === E_PlatformPortalEntry.Monitor) {
          return getters.largeScreenEntryRouterName
        }
        if (entry === E_PlatformPortalEntry.LargeScreen) {
          return E_RouterNames.platformPortal
        }
        return getters.defaultMenuRouterName
      },
      menuPermissionSet(state) {
        const permissions = []
        collectMenuPermissions(state.menus, permissions)
        return new Set(permissions)
      },
      expireTime(state) {
        const account: ICurrentUserInfo = state.account || {}
        return account.expireTime
      },
      needAlertSoonExpire(state) {
        const account: ICurrentUserInfo = state.account || {}
        return account.isSoonExpire === E_IsSoonExpire.Alert && !state.hasAlertSoonExpire
      },
    },
    mutations: {
      updateLoading(state, loading: boolean) {
        state.loading = loading
      },
      updateAccount(state, account) {
        state.account = account
      },
      updateMenus(state, menus) {
        state.menus = menus
        state.menuPermissionsLoaded = true
      },
      setMenuPermissionsLoaded(state, loaded: boolean) {
        state.menuPermissionsLoaded = loaded
      },
      clearStates(state) {
        state.account = null
        state.menus = []
        state.menuPermissionsLoaded = false
        state.platformSetting = {} as any
        state.platformPortal = null
        state.conclusionRepositorList = []
      },
      setHasAlertSoonExpire(state, value: boolean) {
        state.hasAlertSoonExpire = value
      },
      markHasAlertSoonExpire(state) {
        state.hasAlertSoonExpire = true
        const key = buildAlertSoonExpirKey(state.account.user.id)
        storageValueWithTTL(key, 'true', toSeconds(24, E_TimeUnit.Hour))
      },
      updatePlatformSetting(state, value) {
        state.platformSetting = value
      },
      updatePlatformPortal(state, value) {
        state.platformPortal = value
      },
      updateConclusionRepositorList(state, value) {
        state.conclusionRepositorList = value
      }
    },
    actions: {
      async getConclusionRepositorList(context) {
        if (!_.isEmpty(context.state.conclusionRepositorList)) {
          return context.state.conclusionRepositorList
        }
        const res = await diagnosisConclusionRepositorList()
        const data = res.data || []
        context.commit('updateConclusionRepositorList', data)
        return data
      },
      async autoLogin(context, userName) {
        const res = await autoLogin({
          userName,
        })
        if (res.code === 200) {
          markAuth(res.data)
        } else {
          throw new Error("autoLogin failed")
        }
        return context.dispatch('fetchAccountInfoWithMenu')
      },
      async fetchAccountInfoWithMenu(context) {
        context.commit('updateLoading', true)
        context.commit('setMenuPermissionsLoaded', false)
        try {
          const accountRes = await userCurrentInfo()
          loginCounter.incr()
          context.state.features = new Features(accountRes.data.user.parentCompanyId)
          context.commit('updateAccount', accountRes.data)
          const key = buildAlertSoonExpirKey(accountRes.data.user.id)
          context.commit('setHasAlertSoonExpire', getStorageValueWithTTL(key) || false)
          const menuRes = await userCurrentSysMenus()
          context.commit('updateMenus', menuRes.data)
          const portalRes = await platformPortal()
          context.commit('updatePlatformPortal', portalRes.data)
          const userData = accountRes.data
          if (!isSuperAdmin(userData)) {
            const user = userData.user            
            const platformSetting = await platformCompanyGetCompany({
              platformCompanyId: user.parentCompanyId,
              languageType: (i18n?.locale || 'cn') as E_Lang,
              companyId: user.companyId,
            })
            context.commit('updatePlatformSetting', platformSetting.data)
          }
        } finally {
          context.commit('updateLoading', false)
        }
      }
    },
  })
  return store
}

function buildAlertSoonExpirKey(userId) {
  return `alert_soon_expire__${userId}`
}
